
import { useState } from 'react';

export default function LeaveReview() {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (rating === 0) {
      setError('Please select a star rating before submitting.');
      return;
    }
    setError('');
    setSubmitting(true);

    const form = e.currentTarget;
    const data = new URLSearchParams();
    data.append('name', (form.elements.namedItem('name') as HTMLInputElement).value);
    data.append('email', (form.elements.namedItem('email') as HTMLInputElement).value);
    data.append('location', (form.elements.namedItem('location') as HTMLInputElement).value);
    data.append('rating', String(rating) + ' stars');
    data.append('review', (form.elements.namedItem('review') as HTMLTextAreaElement).value);
    data.append('service', (form.elements.namedItem('service') as HTMLSelectElement).value);

    try {
      await fetch('https://readdy.ai/api/form/d6jn8fvrgrhbthj8n1ug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const ratingLabels = ['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent'];

  return (
    <section className="py-20 px-6 bg-gradient-to-br from-amber-50 via-white to-green-50">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-amber-100 rounded-full px-5 py-2 mb-5">
            <i className="ri-star-fill text-amber-600 text-sm"></i>
            <span className="text-amber-700 text-sm font-semibold tracking-wide">Share Your Experience</span>
          </div>
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-neutral-800 mb-4">
            Leave a Review
          </h2>
          <p className="text-neutral-600 text-lg max-w-xl mx-auto leading-relaxed">
            Your story matters. Help other families find the support they deserve by sharing your experience with Lotus Maternal.
          </p>
        </div>

        {submitted ? (
          <div className="bg-white rounded-3xl shadow-xl p-12 text-center border border-amber-100">
            <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-6">
              <i className="ri-heart-fill text-green-600 text-4xl"></i>
            </div>
            <h3 className="font-display text-2xl font-bold text-neutral-800 mb-3">Thank You So Much!</h3>
            <p className="text-neutral-600 leading-relaxed max-w-md mx-auto">
              Your review has been received. We're so grateful you took the time to share your journey — it means the world to us and to the families who will read it.
            </p>
            <button
              onClick={() => { setSubmitted(false); setRating(0); setCharCount(0); }}
              className="mt-8 inline-flex items-center gap-2 bg-amber-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-amber-700 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              Leave Another Review
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-3xl shadow-xl border border-amber-100 overflow-hidden">
            {/* Top accent bar */}
            <div className="h-2 bg-gradient-to-r from-amber-400 via-pink-400 to-green-400"></div>

            <form
              data-readdy-form
              id="customer-review-form"
              onSubmit={handleSubmit}
              className="p-8 lg:p-10 space-y-6"
            >
              {/* Star Rating */}
              <div className="text-center py-4">
                <p className="text-neutral-700 font-semibold mb-4 text-base">How would you rate your experience?</p>
                <div className="flex justify-center gap-3 mb-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      className="w-12 h-12 flex items-center justify-center transition-transform hover:scale-110 cursor-pointer"
                    >
                      <i
                        className={`ri-star-${(hoverRating || rating) >= star ? 'fill' : 'line'} text-4xl transition-colors ${
                          (hoverRating || rating) >= star ? 'text-amber-400' : 'text-neutral-300'
                        }`}
                      ></i>
                    </button>
                  ))}
                </div>
                {(hoverRating || rating) > 0 && (
                  <p className="text-amber-600 font-semibold text-sm">
                    {ratingLabels[hoverRating || rating]}
                  </p>
                )}
                {error && error.includes('rating') && (
                  <p className="text-red-500 text-sm mt-1">{error}</p>
                )}
              </div>

              {/* Name & Email */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-neutral-700 mb-2">
                    Your Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="e.g. Jasmine W."
                    className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-sm text-neutral-800 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-neutral-700 mb-2">
                    Email Address <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="your@email.com"
                    className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-sm text-neutral-800 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all"
                  />
                </div>
              </div>

              {/* Location & Service */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-neutral-700 mb-2">City / Location</label>
                  <input
                    type="text"
                    name="location"
                    placeholder="e.g. Brooklyn, NY"
                    className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-sm text-neutral-800 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-neutral-700 mb-2">Service Received</label>
                  <select
                    name="service"
                    className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-sm text-neutral-800 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all cursor-pointer bg-white"
                  >
                    <option value="">Select a service...</option>
                    <option value="Prenatal Support">Prenatal Support</option>
                    <option value="Birth Doula">Birth Doula</option>
                    <option value="Postpartum Care">Postpartum Care</option>
                    <option value="Lactation Support">Lactation Support</option>
                    <option value="Free Consultation">Free Consultation</option>
                    <option value="Princess Package">Princess Package</option>
                    <option value="Queen Package">Queen Package</option>
                    <option value="Empress Package">Empress Package</option>
                    <option value="Goddess Package">Goddess Package</option>
                    <option value="Full Journey Package">Full Journey Package</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              {/* Review Text */}
              <div>
                <label className="block text-sm font-semibold text-neutral-700 mb-2">
                  Your Review <span className="text-red-400">*</span>
                </label>
                <textarea
                  name="review"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="Tell us about your experience with Lotus Maternal. How did our support make a difference for you and your family?"
                  onChange={(e) => {
                    if (e.target.value.length > 500) return;
                    setCharCount(e.target.value.length);
                  }}
                  className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-sm text-neutral-800 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all resize-none"
                />
                <div className="flex justify-between items-center mt-1">
                  <span className="text-xs text-neutral-400">Share as much or as little as you'd like</span>
                  <span className={`text-xs font-medium ${charCount >= 480 ? 'text-red-400' : 'text-neutral-400'}`}>
                    {charCount}/500
                  </span>
                </div>
              </div>

              {error && !error.includes('rating') && (
                <p className="text-red-500 text-sm text-center">{error}</p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-gradient-to-r from-amber-500 to-amber-600 text-white py-4 rounded-xl font-semibold text-base hover:from-amber-600 hover:to-amber-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Submitting...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill"></i>
                    Submit My Review
                  </>
                )}
              </button>

              <p className="text-center text-xs text-neutral-400">
                Your email will not be published. Reviews may be shared on our website with your permission.
              </p>
            </form>
          </div>
        )}
      </div>
    </section>
  );
}
